import { apiRequest } from './apiClient';
import {
  ShoppingListSimple,
  ShoppingListWithProducts,
  ShoppingListCreateRequest,
  ShoppingListUpdateRequest,
  ChangeProductStatusRequest,
} from '../types/shoppingList';

export const getShoppingLists = async (status?: string, page?: number): Promise<ShoppingListSimple[]> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (page) params.append('page', page.toString());

  const url = params.toString() ? `api/shopping-lists?${params.toString()}` : 'api/shopping-lists';
  const response = await apiRequest.get(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to retrieve shopping lists');
  }

  return response.json();
};

export const getShoppingList = async (listId: string): Promise<ShoppingListWithProducts> => {
  const response = await apiRequest.get(`api/shopping-lists/${listId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'An unexpected error occurred');
  }

  return response.json();
};

export const createShoppingList = async (
  data: ShoppingListCreateRequest
): Promise<{ message: string; id: string }> => {
  const response = await apiRequest.post('api/shopping-lists', data);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'List creation failed');
  }

  return response.json();
};

export const updateShoppingList = async (listId: string, data: ShoppingListUpdateRequest): Promise<void> => {
  const response = await apiRequest.patch(`api/shopping-lists/${listId}`, data);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'List update failed');
  }
};

export const deleteShoppingList = async (listId: string): Promise<void> => {
  const response = await apiRequest.delete(`api/shopping-lists/${listId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'List deletion failed');
  }
};

export const copyShoppingList = async (
  listId: string,
  newName: string
): Promise<{ message: string; id: string }> => {
  const response = await apiRequest.post(`api/shopping-lists/${listId}/copy`, { name: newName });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'List copy failed');
  }

  return response.json();
};

export const changeProductStatus = async (
  listId: string,
  productId: string,
  data: ChangeProductStatusRequest
): Promise<void> => {
  const response = await apiRequest.patch(`api/shopping-lists/${listId}/products/${productId}/status`, data);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Product status change failed');
  }
};

export const getPublicShoppingList = async (listId: string): Promise<ShoppingListWithProducts> => {
  const response = await apiRequest.get(`api/public-shopping-lists/${listId}`, { requiresHouseholdId: false });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'An unexpected error occurred');
  }

  return response.json();
};

export const changePublicProductStatus = async (
  listId: string,
  productId: string,
  data: ChangeProductStatusRequest
): Promise<void> => {
  const response = await apiRequest.patch(`api/public-shopping-lists/${listId}/products/${productId}/status`, data, {
    requiresHouseholdId: false,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Product status change failed');
  }
};